import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import * as notificationsApi from "@/api/notifications";
import type {
  NotificationResponse,
  NotificationListResponse,
} from "@/types/user";

interface UseNotificationsReturn {
  /** List of recent notifications (most recent first). */
  notifications: NotificationResponse[];
  /** Total unread notification count. */
  unreadCount: number;
  /** Whether the initial fetch is still in progress. */
  isLoading: boolean;
  /** Whether the polling connection is active. */
  isConnected: boolean;
  /** Mark a single notification as read. Removes the unread indicator locally. */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read. Resets the unread count to zero. */
  markAllAsRead: () => Promise<void>;
  /** Add a notification to the local list (used by SSE or other real-time sources). */
  addNotification: (notification: NotificationResponse) => void;
  /** Force-refresh notifications from the server. */
  refresh: () => Promise<void>;
}

/** Polling interval in milliseconds (15 seconds). */
const POLL_INTERVAL_MS = 15_000;

/**
 * Hook for managing the current user's notification state.
 *
 * On mount (when authenticated), fetches the first page of notifications and
 * the unread count. Then polls the unread count endpoint at a regular interval
 * so the notification bell badge stays up-to-date without requiring SSE.
 *
 * If SSE support is added later, the `addNotification` function can be called
 * from an SSE event handler to push new notifications into the local list
 * without waiting for the next poll cycle.
 *
 * All state is cleared when the user logs out.
 */
export function useNotifications(): UseNotificationsReturn {
  const { isAuthenticated } = useAuth();

  const [notifications, setNotifications] = useState<NotificationResponse[]>(
    [],
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Ref to track whether the component is still mounted, preventing state
  // updates after unmount.
  const mountedRef = useRef(true);

  // Ref to hold the polling interval ID so we can clean it up.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch initial data ───────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setIsConnected(false);
      return;
    }

    try {
      setIsLoading(true);
      const response: NotificationListResponse =
        await notificationsApi.getNotifications({
          page: 1,
          per_page: 20,
        });

      if (mountedRef.current) {
        setNotifications(response.data);
        setUnreadCount(response.unread_count);
        setIsConnected(true);
      }
    } catch (error) {
      console.error(
        "[useNotifications] Failed to fetch notifications:",
        error,
      );
      // Keep whatever state we had — stale data is better than nothing.
      if (mountedRef.current) {
        setIsConnected(false);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  // ── Poll for unread count ────────────────────────────────────────────────

  const pollUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const count = await notificationsApi.getUnreadCount();
      if (mountedRef.current) {
        setUnreadCount(count);
        setIsConnected(true);
      }
    } catch {
      // Silently fail — the next poll will try again.
      if (mountedRef.current) {
        setIsConnected(false);
      }
    }
  }, [isAuthenticated]);

  // ── Setup: initial fetch + polling ───────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;

    if (isAuthenticated) {
      fetchNotifications();

      // Start polling for unread count changes
      pollIntervalRef.current = setInterval(pollUnreadCount, POLL_INTERVAL_MS);
    } else {
      // User logged out — reset state
      setNotifications([]);
      setUnreadCount(0);
      setIsConnected(false);
    }

    return () => {
      mountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, fetchNotifications, pollUnreadCount]);

  // ── Re-fetch on window focus (browser may have throttled background tabs) ─

  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated) {
        pollUnreadCount();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isAuthenticated, pollUnreadCount]);

  // ── Public API ───────────────────────────────────────────────────────────

  const markAsRead = useCallback(
    async (notificationId: string): Promise<void> => {
      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await notificationsApi.markAsRead(notificationId);
      } catch (error) {
        console.error(
          "[useNotifications] Failed to mark notification as read:",
          error,
        );
        // Revert optimistic update on failure — refetch from server
        await fetchNotifications();
        throw error;
      }
    },
    [fetchNotifications],
  );

  const markAllAsRead = useCallback(async (): Promise<void> => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true })),
    );
    setUnreadCount(0);

    try {
      await notificationsApi.markAllAsRead();
    } catch (error) {
      console.error(
        "[useNotifications] Failed to mark all notifications as read:",
        error,
      );
      // Revert optimistic update on failure
      await fetchNotifications();
      throw error;
    }
  }, [fetchNotifications]);

  const addNotification = useCallback(
    (notification: NotificationResponse): void => {
      setNotifications((prev) => {
        // Prevent duplicates (in case of SSE + polling race)
        if (prev.some((n) => n.id === notification.id)) {
          return prev;
        }
        // Prepend the new notification to the top of the list
        return [notification, ...prev];
      });

      if (!notification.is_read) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    [],
  );

  const refresh = useCallback(async (): Promise<void> => {
    await fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    markAsRead,
    markAllAsRead,
    addNotification,
    refresh,
  };
}

export default useNotifications;
